import { useRef, useState } from "react";
import { Loader2, Upload, Wand2, AlertTriangle, Save, Volume2, Mic } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { uploadFile, promoNarrationPreview } from "@/lib/api";
import { ClosingStatement, WelcomeStatement } from "./ClosingStatement";

const F = ({ label, hint, children, testId }) => (
  <div data-testid={testId}><Label className="text-xs text-zinc-600">{label}</Label>{children}{hint && <div className="text-[11px] text-zinc-400 mt-1">{hint}</div>}</div>
);

async function pngHasTransparency(file) {
  if (file.type !== "image/png") return false;
  const bmp = await createImageBitmap(file);
  const c = document.createElement("canvas");
  c.width = Math.min(200, bmp.width); c.height = Math.min(200, bmp.height);
  const ctx = c.getContext("2d");
  ctx.drawImage(bmp, 0, 0, c.width, c.height);
  const d = ctx.getImageData(0, 0, c.width, c.height).data;
  for (let i = 3; i < d.length; i += 4) if (d[i] < 250) return true;
  return false;
}

function ImagePick({ label, value, onChange, userRecordId, kind, hint, testId, onWarn }) {
  const ref = useRef(null);
  const [up, setUp] = useState(false);
  async function pick(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUp(true);
    try {
      if (kind === "logo") onWarn(!(await pngHasTransparency(file)));
      const r = await uploadFile(file, userRecordId, kind, "");
      onChange(r.url || r.public_url || r.item?.url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUp(false);
    }
  }
  return (
    <F label={label} hint={hint} testId={testId}>
      <div className="flex items-center gap-3 mt-1">
        <div className="h-16 w-16 rounded-xl border border-zinc-200 bg-[linear-gradient(45deg,#eee_25%,transparent_25%,transparent_75%,#eee_75%),linear-gradient(45deg,#eee_25%,#fff_25%,#fff_75%,#eee_75%)] bg-[length:12px_12px] bg-[position:0_0,6px_6px] overflow-hidden flex items-center justify-center shrink-0">
          {value ? <img src={value} alt="" className="max-h-full max-w-full object-contain" /> : <span className="text-[10px] text-zinc-400">none</span>}
        </div>
        <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-1.5">
          <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="https://…" className="h-9 rounded-lg text-xs flex-1 min-w-0" data-testid={`${testId}-url`} />
          <button type="button" onClick={() => ref.current?.click()} disabled={up} className="h-9 px-3 rounded-lg border border-zinc-200 text-xs font-semibold inline-flex items-center justify-center gap-1.5 hover:bg-zinc-50 disabled:opacity-50 shrink-0" data-testid={`${testId}-upload`}>
            {up ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Upload
          </button>
          <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} />
        </div>
      </div>
    </F>
  );
}

export function PromoForm({ form, setForm, userRecordId, onGenerate, onSave, busy, disabled, templateName, saved, templateId, template, myVoices = [] }) {
  const [logoWarn, setLogoWarn] = useState(false);
  const [hearing, setHearing] = useState("");
  const [heard, setHeard] = useState("");
  const audioRef = useRef(null);
  async function hear(sec) {
    setHearing(sec);
    try {
      const r = await promoNarrationPreview(userRecordId, userRecordId, form, templateId, sec);
      audioRef.current?.pause();
      const a = new Audio(URL.createObjectURL(r.blob));
      audioRef.current = a;
      setHeard(r.text);
      await a.play();
    } catch (e) {
      let msg = e?.response?.data?.detail;
      if (!msg && e?.response?.data instanceof Blob) { try { msg = JSON.parse(await e.response.data.text()).detail; } catch { /* ignore */ } }
      toast.error(msg || "Could not play the narrator preview");
    } finally {
      setHearing("");
    }
  }
  const canHear = (form.spoken_name || form.business_name) && form.owner_name;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const req = ["business_name", "owner_name", "phone", "logo_url", "owner_photo_url"].filter((k) => !(form[k] || "").trim());
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4 sm:p-5 space-y-4" data-testid="promo-form">
      <div className="text-sm font-semibold text-zinc-900"><span className="inline-flex h-5 w-5 rounded-full bg-zinc-900 text-white text-[11px] items-center justify-center mr-2">2</span>Brand it <span className="text-xs font-normal text-zinc-400">— {saved ? "your saved details, reused for every video" : "pre-filled from your profile; saved for every video"}</span></div>
      <div className="grid sm:grid-cols-2 gap-3">
        <F label="Business name (on screen, max 40)" testId="promo-f-business"><Input maxLength={40} value={form.business_name} onChange={set("business_name")} className="h-10 rounded-xl mt-1" /></F>
        <F label="Business name pronunciation (for the narrator)" hint="Spell it like it sounds — e.g. Bocci → Bouchi" testId="promo-f-spoken"><Input value={form.spoken_name} onChange={set("spoken_name")} className="h-10 rounded-xl mt-1" /></F>
        <F label="Your name (on screen)" testId="promo-f-owner"><Input value={form.owner_name} onChange={set("owner_name")} className="h-10 rounded-xl mt-1" /></F>
        <F label="Your name pronunciation (for the narrator)" hint="Leave blank if it's said the way it's spelled" testId="promo-f-spoken-owner"><Input value={form.spoken_owner || ""} onChange={set("spoken_owner")} placeholder={form.owner_name || "e.g. Lora Co-en"} className="h-10 rounded-xl mt-1" /></F>
        <F label="Your title" hint="On screen and spoken by the narrator — e.g. Owner, Managing Director, Lead Esthetician" testId="promo-f-title"><Input value={form.owner_title || ""} onChange={set("owner_title")} placeholder="Owner" className="h-10 rounded-xl mt-1" /></F>
      </div>
      <div className="flex flex-wrap items-center gap-3 rounded-xl bg-zinc-50 border border-zinc-100 p-2.5" data-testid="promo-narration-preview">
        <label className="text-xs font-semibold text-zinc-700 inline-flex items-center gap-1.5" data-testid="promo-narrator-label">
          <Mic size={13} className="text-[#c9a34a]" /> Narrator
          <select value={form.voice_id || ""} onChange={set("voice_id")} className="h-9 rounded-lg border border-zinc-200 bg-white px-2 text-xs font-normal" data-testid="promo-narrator-select">
            <option value="">OreVital narrator (Melissa)</option>
            {myVoices.map((v) => <option key={v.voice_id} value={v.voice_id}>★ {v.name} (my voice)</option>)}
          </select>
        </label>
        {!myVoices.length && <span className="text-[11px] text-zinc-500" data-testid="promo-narrator-hint">Want it in your own voice? Clone it once in <b>Slides → Voice → My own voice</b> and it shows up here.</span>}
        <button type="button" onClick={() => hear("intro")} disabled={!!hearing || !canHear} className="h-9 px-3 rounded-full bg-zinc-900 text-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-40 hover:bg-zinc-800 transition-colors" data-testid="promo-hear-narrator-btn">
          {hearing === "intro" ? <Loader2 size={13} className="animate-spin" /> : <Volume2 size={13} />} Hear my names & title
        </button>
        <div className="text-[11px] text-zinc-500 flex-1 min-w-[160px]">{heard ? <span data-testid="promo-narration-text">“{heard}”</span> : "A 5-second check of how your business, name and title will sound — fix the pronunciation fields and listen again."}</div>
      </div>
      {template && <WelcomeStatement template={template} form={form} setForm={setForm} onHear={hear} hearing={canHear ? hearing : "disabled"} />}
      {template && <ClosingStatement template={template} form={form} setForm={setForm} onHear={hear} hearing={canHear ? hearing : "disabled"} />}
      <div className="grid gap-3">
        <ImagePick label="Logo (transparent PNG)" value={form.logo_url} onChange={(v) => setForm((f) => ({ ...f, logo_url: v }))} userRecordId={userRecordId} kind="logo" testId="promo-f-logo" onWarn={setLogoWarn} hint="No white box: use a PNG with a transparent background" />
        <ImagePick label="Owner photo" value={form.owner_photo_url} onChange={(v) => setForm((f) => ({ ...f, owner_photo_url: v }))} userRecordId={userRecordId} kind="photo" testId="promo-f-photo" onWarn={() => {}} hint="Portrait, at least 600px" />
      </div>
      {logoWarn && <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-100 text-amber-800 text-xs p-2.5" data-testid="promo-logo-warning"><AlertTriangle size={14} className="mt-0.5 shrink-0" /> This logo has no transparency — it will show as a box on the video. Upload a transparent PNG if you can.</div>}
      <div className="grid sm:grid-cols-2 gap-3">
        <F label="Address (one line per row)" testId="promo-f-address"><Textarea rows={2} value={form.address} onChange={set("address")} className="rounded-xl mt-1 text-sm" /></F>
        <div className="grid gap-3">
          <F label="Phone" testId="promo-f-phone"><Input value={form.phone} onChange={set("phone")} className="h-10 rounded-xl mt-1" /></F>
          <F label="Website" testId="promo-f-website"><Input value={form.website} onChange={set("website")} placeholder="www.yourspa.com" className="h-10 rounded-xl mt-1" /></F>
        </div>
        <F label="Book online" testId="promo-f-booking"><Input value={form.booking} onChange={set("booking")} placeholder="yourspa.com/book" className="h-10 rounded-xl mt-1" /></F>
        <F label="Instagram (optional)" testId="promo-f-instagram"><Input value={form.instagram} onChange={set("instagram")} placeholder="@yourspa" className="h-10 rounded-xl mt-1" /></F>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
        <div className="text-xs text-zinc-500">{req.length ? `Still needed: ${req.map((k) => k.replace(/_url$/, "").replace("_", " ")).join(", ")}` : `Everything's in — review the preview, then generate your ${templateName || "video"}.`}</div>
        <div className="flex items-center gap-2">
        <button type="button" onClick={onSave} disabled={busy} className="h-11 px-4 rounded-full border border-zinc-200 bg-white text-zinc-800 text-sm font-semibold inline-flex items-center gap-2 hover:bg-zinc-50 disabled:opacity-50 transition-colors" data-testid="promo-save-details-btn">
          <Save size={14} /> Save details
        </button>
        <button type="button" onClick={onGenerate} disabled={busy || disabled || req.length > 0} className="h-11 px-5 rounded-full bg-amber-500 hover:bg-amber-400 text-zinc-900 text-sm font-bold inline-flex items-center gap-2 disabled:opacity-50 transition-colors" data-testid="promo-generate-btn">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />} Brand it & Generate
        </button>
        </div>
      </div>
    </div>
  );
}
